<script lang="ts">
  import { browser } from '$app/environment';

  enum Position {
    Default,
    Top,
    Bottom
  }

  const ThresholdValue = 10; // in pixel

  interface Props {
    show?: boolean;
    hasOverflow?: boolean;
    autoAlign?: boolean;
    label?: import('svelte').Snippet;
    list?: import('svelte').Snippet;
    onclose?: () => void;
  }

  let {
    show = false,
    hasOverflow = false,
    autoAlign = false,
    label,
    list,
    onclose
  }: Props = $props();

  let parent: HTMLElement = $state();
  let labelElement: HTMLElement = $state();
  let listElement: HTMLElement = $state();

  const onClickWindow = (e: any) => {
    if (parent && !parent.contains(e.target)) {
      onclose?.();
      removeScrollEventListner();
    }
  };

  const addScrollEventListener = (): void => {
    browser && window.addEventListener('svelte-scroll', () => setPosition());
  };

  const removeScrollEventListner = (): void => {
    browser && window.removeEventListener('svelte-scroll', () => setPosition());
  };

  const getListHeight = (): number => {
    return listElement?.getBoundingClientRect()?.height || 0;
  };

  const calculatePosition = (dimension: DOMRect): { x: number; y: number } => {
    const position = (autoAlign && findPosition(dimension)) || Position.Default;
    let x = 0;
    let y = 0;
    const listHeight = getListHeight();
    switch (position) {
      case Position.Top: {
        x = dimension.x;
        y = dimension.y - listHeight + 10;
        break;
      }
      case Position.Bottom: {
        x = dimension.x;
        y = dimension.y + dimension.height + 6;
        break;
      }
      default: {
        x = dimension.left;
        y = dimension.top + dimension.height + 6;
      }
    }
    return { x, y };
  };

  const setPosition = (): void => {
    const dim = labelElement?.getBoundingClientRect();
    if (!dim) return;

    const { x, y } = calculatePosition(dim);
    listElement.style.top = y + 'px';
    listElement.style.left = x + 'px';
    listElement.style.width = dim.width + 'px';
  };

  const findPosition = (dimension: DOMRect): Position => {
    const { top, bottom } = dimension;
    const windowHeight = window.innerHeight - ThresholdValue;
    const listHeight = getListHeight();
    // Check if there's enough space at the bottom
    const hasSpaceAtBottom = bottom + listHeight < windowHeight;

    if (hasSpaceAtBottom) return Position.Bottom;

    // Check if there's enough space at the top
    const hasSpaceAtTop = top - listHeight > ThresholdValue;
    if (hasSpaceAtTop) return Position.Top;

    // Default to bottom if neither has enough space
    return Position.Bottom;
  };

  export const setListPosition = (): void => {
    if (!hasOverflow) return;

    addScrollEventListener();
    setPosition();
  };
</script>

<svelte:window onmouseup={onClickWindow} />

<div bind:this={parent} class="comp-app-with-list">
  <div bind:this={labelElement} class="label">
    {@render label?.()}
  </div>

  <div bind:this={listElement} class="list" class:show class:overflow={hasOverflow}>
    {@render list?.()}
  </div>
</div>

<style>
  .comp-app-with-list {
    position: relative;
    width: inherit;
  }
  .list {
    display: none;
  }
  .list.overflow {
    position: fixed;
    top: calc(100% + 8px);
    right: 0;
    z-index: 99;
  }
  .list.show {
    display: block;
  }
</style>
